/**
 * feat/wizard-duplicate-sequence-guard — ONE place for the New Submission
 * wizard's sequence-number rules: normalisation ("1" ≡ "0001"), next-free
 * sequence, and the "is this sequence already taken for this application +
 * region" validator. The wizard, its tests and the server-side 409 handler
 * all speak in these terms, so "1" and "0001" can never be judged differently
 * on two surfaces.
 *
 * Region identifier labels are here too so the blocking message names the
 * identifier the operator actually typed ("NDA 900900", "Dossier ID …").
 */
import type { Region, Submission } from '@/api/v2Client';
import { occupiesSequenceSlot as sharedOccupiesSequenceSlot } from '@shared/sequenceSlot';
import { initialSequenceNumberForRegion } from '@shared/initialSequence';

/** Parse a zero-padded sequence string to an int (NaN-safe → -1). */
export function parseSeq(s: string | null | undefined): number {
  const n = Number.parseInt(String(s ?? '').trim(), 10);
  return Number.isFinite(n) ? n : -1;
}

type SlotFields = Pick<Submission, 'Status' | 'EsgAckReceivedAt' | 'EsgAckPayload'>;

/**
 * A sequence row occupies its slot UNLESS the gateway technically rejected it
 * (Status 'Rejected' with a received ESG rejection acknowledgement). This is
 * THE server predicate (`@shared/sequenceSlot`, also used by
 * `duplicateSequence.ts`, the first-sequence guard and the validator), not a
 * copy: a bare 'Rejected' or 'Withdrawn' row keeps its number, so the wizard
 * never offers a slot the server would refuse.
 */
export function occupiesSequenceSlot(row: SlotFields): boolean {
  return sharedOccupiesSequenceSlot(row);
}

/** The subset of rows that actually occupy a sequence slot. */
export function liveSequences<T extends SlotFields>(
  rows: readonly T[],
): readonly T[] {
  return rows.filter(occupiesSequenceSlot);
}

/** Canonical 4-digit form, or null when the input is not a sequence number. */
export function normalizeSeq(s: string | null | undefined): string | null {
  const n = parseSeq(s);
  return n < 0 ? null : String(n).padStart(4, '0');
}

/** V1 parity (`updateSeqForNda`): next sequence = max(existing) + 1, padded. */
export function nextSequenceNumber(existing: readonly Pick<Submission, 'SequenceNumber'>[]): string {
  const max = existing.reduce((m, s) => Math.max(m, parseSeq(s.SequenceNumber)), -1);
  return String(Math.max(max + 1, 0)).padStart(4, '0');
}

/**
 * True when any existing sequence of the application equals `sequenceNumber`
 * after normalising BOTH sides ("1" and "0001" are the same sequence).
 * An unparseable candidate is never "taken" — required-field validation
 * reports that case separately.
 */
export function isSequenceTaken(
  existing: readonly Pick<Submission, 'SequenceNumber'>[],
  sequenceNumber: string | null | undefined,
): boolean {
  const target = parseSeq(sequenceNumber);
  if (target < 0) return false;
  return existing.some((s) => parseSeq(s.SequenceNumber) === target);
}

/** The existing row that occupies `sequenceNumber`, if any (for the message/link). */
export function findTakenBy<T extends Pick<Submission, 'SequenceNumber'>>(
  existing: readonly T[],
  sequenceNumber: string | null | undefined,
): T | null {
  const target = parseSeq(sequenceNumber);
  if (target < 0) return null;
  return existing.find((s) => parseSeq(s.SequenceNumber) === target) ?? null;
}

/** Human label of the identifier that names an application in this region. */
export function identifierLabel(region: Region | string): string {
  if (region === 'FDA') return 'NDA';
  if (region === 'EMA' || region === 'MHRA') return 'EU application UUID';
  if (region === 'Health Canada') return 'Dossier ID';
  return 'application identifier';
}

/**
 * The blocking message for a taken sequence, e.g.
 * "Sequence 0000 already exists for NDA 900900 (FDA). Next available sequence is 0001."
 */
export function duplicateSequenceMessage(args: {
  readonly region: Region | string;
  readonly identifier: string;
  readonly sequenceNumber: string;
  readonly existing: readonly Pick<Submission, 'SequenceNumber'>[];
}): string {
  const seq = normalizeSeq(args.sequenceNumber) ?? args.sequenceNumber;
  const next = nextSequenceNumber(args.existing);
  return (
    `Sequence ${seq} already exists for ${identifierLabel(args.region)} ${args.identifier} ` +
    `(${args.region}). Next available sequence is ${next}.`
  );
}

/**
 * The blocking message on the ORIGINAL path (sequence locked to the region's
 * initial number — 0001 for FDA and TGA, 0000 elsewhere): the application
 * already exists, so this cannot be a new original.
 */
export function existingApplicationMessage(args: {
  readonly region: Region | string;
  readonly identifier: string;
  readonly existing: readonly Pick<Submission, 'SequenceNumber'>[];
}): string {
  const n = args.existing.length;
  return (
    `An application with ${identifierLabel(args.region)} ${args.identifier} already exists ` +
    `(${args.region}, ${n} sequence${n === 1 ? '' : 's'}). A new original cannot be created ` +
    `at ${initialSequenceNumberForRegion(args.region)} — file it as a supplement instead.`
  );
}
